import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import BenefitCard from './components/BenefitCard';
import TrustBadge from './components/TrustBadge';
import PasswordStrengthIndicator from './components/PasswordStrengthIndicator';
import TermsModal from './components/TermsModal';
import { useAuth } from '../../contexts/AuthContext';

export default function Register() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [errors, setErrors] = useState({});

  const businessTypes = [
  { value: 'small_business', label: 'Small Business (1-10 employees)' },
  { value: 'medium_business', label: 'Medium Business (11-50 employees)' },
  { value: 'agency', label: 'Digital Marketing Agency' },
  { value: 'creator', label: 'Content Creator / Influencer' },
  { value: 'enterprise', label: 'Enterprise (50+ employees)' },
  { value: 'freelancer', label: 'Freelancer / Consultant' }];


  const benefits = [
  {
    icon: 'Share2',
    title: 'Multi-Platform Integration',
    description: 'Connect and manage Facebook, Instagram, Twitter, LinkedIn, and TikTok accounts from one unified dashboard.'
  },
  {
    icon: 'Calendar',
    title: 'Automated Posting',
    description: 'Schedule content across all platforms with intelligent timing optimization for maximum engagement.'
  },
  {
    icon: 'TrendingUp',
    title: 'Organic Growth Analytics',
    description: 'Track your organic reach, engagement rates, and follower growth with detailed performance insights.'
  },
  {
    icon: 'MessageSquare',
    title: 'Strategic Engagement',
    description: 'Automate comment placement on target timelines to increase visibility and drive organic traffic.'
  },
  {
    icon: 'Users',
    title: 'Audience Targeting',
    description: 'Identify and engage with your ideal audience through advanced segmentation and targeting tools.'
  },
  {
    icon: 'BarChart3',
    title: 'Campaign Management',
    description: 'Create and track coordinated campaigns across multiple platforms for consistent brand messaging.'
  }];


  const trustBadges = [
  { icon: 'Shield', text: 'SSL Secured' },
  { icon: 'Lock', text: 'GDPR Compliant' },
  { icon: 'CheckCircle', text: 'Official API Certified' }];


  const passwordRequirements = [
  { met: formData?.password?.length >= 8, text: 'At least 8 characters' },
  { met: /[A-Z]/?.test(formData?.password), text: 'One uppercase letter' },
  { met: /[a-z]/?.test(formData?.password), text: 'One lowercase letter' },
  { met: /\d/?.test(formData?.password), text: 'One number' },
  { met: /[^a-zA-Z0-9]/?.test(formData?.password), text: 'One special character' }];


  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordRequirements?.every((req) => req?.met)) {
      newErrors.password = 'Password does not meet all requirements';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.businessType) {
      newErrors.businessType = 'Please select your business type';
    }

    if (!formData?.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the Terms of Service';
    }

    if (!formData?.agreePrivacy) {
      newErrors.agreePrivacy = 'You must agree to the Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.agreeTerms || !formData?.agreePrivacy) {
      setError('You must accept the terms and conditions');
      return;
    }

    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setError('');
      await signUp(formData?.email, formData?.password, formData?.fullName);
      setSuccess(true);
    } catch (err) {
      setError(err?.message);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h2>
          <p className="text-gray-600 mb-6">
            We have sent you a confirmation email. Please click the link in the email to verify your account.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">

            Back to Login
          </button>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-accent shadow-lg">
                <Icon name="TrendingUp" size={28} color="#FFFFFF" />
              </div>
              <h1 className="text-3xl font-bold text-foreground">BlueMegaphone</h1>
            </div>
            <p className="text-lg text-muted-foreground">
              Be heard over the crowd
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Registration Form */}
            <div className="bg-card rounded-lg shadow-card border border-border p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  Create Your Account
                </h2>
                <p className="text-sm text-muted-foreground">
                  Join thousands of businesses growing organically
                </p>
              </div>

              {error &&
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              }

              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  label="Full Name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData?.fullName}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  error={errors?.fullName}
                  required />


                <Input
                  label="Email Address"
                  type="email"
                  placeholder="you@company.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  required />


                <div>
                  <div className="relative">
                    <Input
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      value={formData?.password}
                      onChange={(e) => handleInputChange('password', e?.target?.value)}
                      error={errors?.password}
                      required />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-9 p-1 rounded hover:bg-muted transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}>

                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                    </button>
                  </div>
                  <PasswordStrengthIndicator password={formData?.password} />
                  
                  {formData?.password &&
                  <div className="mt-3 p-3 rounded-lg bg-muted/50 border border-border">
                      <p className="text-xs font-medium text-foreground mb-2">Password Requirements:</p>
                      <div className="space-y-1">
                        {passwordRequirements?.map((req, index) =>
                      <div key={index} className="flex items-center gap-2">
                            <Icon
                          name={req?.met ? 'CheckCircle2' : 'Circle'}
                          size={14}
                          color={req?.met ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />

                            <span className={`text-xs ${req?.met ? 'text-success' : 'text-muted-foreground'}`}>
                              {req?.text}
                            </span>
                          </div>
                      )}
                      </div>
                    </div>
                  }
                </div>

                <div className="relative">
                  <Input
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    value={formData?.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                    error={errors?.confirmPassword}
                    required />

                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-9 p-1 rounded hover:bg-muted transition-colors"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>

                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>

                <Select
                  label="Business Type"
                  placeholder="Select your business type"
                  options={businessTypes}
                  value={formData?.businessType}
                  onChange={(value) => handleInputChange('businessType', value)}
                  error={errors?.businessType}
                  required
                  searchable />


                <div className="space-y-3 pt-2">
                  <Checkbox
                    label={
                    <span className="text-sm text-foreground">
                        I agree to the{' '}
                        <button
                        type="button"
                        onClick={() => setModalType('terms')}
                        className="text-primary hover:text-primary/80 font-medium underline">

                          Terms of Service
                        </button>
                      </span>
                    }
                    checked={formData?.agreeTerms}
                    onChange={(e) => handleInputChange('agreeTerms', e?.target?.checked)}
                    error={errors?.agreeTerms} />


                  <Checkbox
                    label={
                    <span className="text-sm text-foreground">
                        I agree to the{' '}
                        <button
                        type="button"
                        onClick={() => setModalType('privacy')}
                        className="text-primary hover:text-primary/80 font-medium underline">

                          Privacy Policy
                        </button>
                      </span>
                    }
                    checked={formData?.agreePrivacy}
                    onChange={(e) => handleInputChange('agreePrivacy', e?.target?.checked)}
                    error={errors?.agreePrivacy} />

                </div>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  loading={isLoading}
                  iconName="UserPlus"
                  iconPosition="left">

                  Create Account
                </Button>

                <div className="text-center pt-4">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="text-primary hover:text-primary/80 font-medium underline">

                      Sign in here
                    </button>
                  </p>
                </div>
              </form>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-2 justify-center mt-6 pt-6 border-t border-border">
                {trustBadges?.map((badge, index) =>
                <TrustBadge key={index} icon={badge?.icon} text={badge?.text} />
                )}
              </div>
            </div>

            {/* Benefits Section */}
            <div className="space-y-6">
              <div className="bg-card rounded-lg shadow-card border border-border p-8">
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Why Choose BlueMegaphone?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Everything you need to grow your social media presence organically
                  </p>
                </div>

                <div className="space-y-4">
                  {benefits?.map((benefit, index) =>
                  <BenefitCard
                    key={index}
                    icon={benefit?.icon}
                    title={benefit?.title}
                    description={benefit?.description} />

                  )}
                </div>
              </div>

              {/* Social Proof */}
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg border border-primary/20 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <Icon name="Quote" size={24} color="var(--color-primary)" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-foreground italic mb-3">
                      "BlueMegaphone transformed our social media strategy. We've seen a 300% increase in organic engagement and our follower growth has been consistently strong month over month."
                    </p>
                    <div className="flex items-center gap-3">
                      <Image
                        src="https://img.rocket.new/generatedImages/rocket_gen_img_1b6661299-1763294473289.png"
                        alt="Professional headshot of Sarah Johnson, marketing director with shoulder-length brown hair wearing navy blazer"
                        className="w-10 h-10 rounded-full object-cover" />

                      <div>
                        <p className="text-sm font-semibold text-foreground">Sarah Johnson</p>
                        <p className="text-xs text-muted-foreground">Marketing Director, TechStart Inc.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-card rounded-lg shadow-card border border-border p-4 text-center">
                  <div className="text-2xl font-bold text-primary mb-1">10K+</div>
                  <div className="text-xs text-muted-foreground">Active Users</div>
                </div>
                <div className="bg-card rounded-lg shadow-card border border-border p-4 text-center">
                  <div className="text-2xl font-bold text-accent mb-1">5M+</div>
                  <div className="text-xs text-muted-foreground">Posts Scheduled</div>
                </div>
                <div className="bg-card rounded-lg shadow-card border border-border p-4 text-center">
                  <div className="text-2xl font-bold text-success mb-1">98%</div>
                  <div className="text-xs text-muted-foreground">Satisfaction</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TermsModal
        isOpen={modalType !== null}
        onClose={() => setModalType(null)}
        type={modalType} />

    </div>);

}